import type { ApolloCache } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client';
import type {
  InsertAssessmentInput,
  InsertOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  GetAllOversightAssessmentsDocument,
  GetOversightAssessmentByIdDocument,
  InsertOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertOversightAssessmentTRPC } from './useInsertOversightAssessmentTRPC';

/**
 * Apollo reads that this mutation invalidates.
 *
 * Shared by both branches on purpose. The migrated tRPC path still has to
 * evict these because the corresponding READS are NOT migrated and are
 * cache-first, so a write via tRPC otherwise leaves the Apollo-backed list or
 * counter showing pre-write data. Keeping one list means the two paths cannot
 * drift — that drift is what produced the stale oversight count.
 *
 * This lives in the WRAPPER, not in the `*TRPC` hook: the wrapper is the
 * flag-aware seam that already owns the Apollo mutation, so the tRPC hook
 * stays free of Apollo.
 */
const evictApolloReads = (cache: ApolloCache<unknown>) => {
  evictField(cache, 'oversight_assessment');
  evictField(cache, 'oversight_assessment_aggregate');
};

export const useInsertOversightAssessment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const apolloClient = useApolloClient();

  // GraphQL mutation
  const [insertOversightAssessmentGraphQL, graphqlState] = useMutation(
    InsertOversightAssessmentDocument,
    {
      update: (cache) => {
        evictApolloReads(cache);
      },
      refetchQueries: [
        GetOversightAssessmentByIdDocument,
        GetAllOversightAssessmentsDocument,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertOversightAssessmentTRPC();

  const insertOversightAssessment = async (
    variables: InsertAssessmentInput
  ): Promise<InsertOversightAssessmentMutation> => {
    if (trpcEnabled) {
      const trpcResult = await trpcMutation.insertOversightAssessment({
        ...variables,
        Title: variables.Title ?? '',
        Summary: variables.Summary ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
      evictApolloReads(apolloClient.cache);

      return trpcResult;
    }

    const result = await insertOversightAssessmentGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to insert oversight assessment');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertOversightAssessment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertOversightAssessment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
